import React, { useState } from "react";
import {
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  ListItemIcon,
  useMediaQuery,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import SettingsIcon from "@mui/icons-material/Settings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import EmailIcon from "@mui/icons-material/Email";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import BadgeIcon from "@mui/icons-material/Badge";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { setUser } from "../../../contextApi/reducer";
import { SUPERADMIN } from "../../../utils/constant/stringConstant";
import { PermissionComponentWrapper } from "../PermissionComponentWrapper";
import { messages } from "../../../locales/messages";
import { routes } from "../../../utils/constant/navigationRoutes";
import { clearAuthToken } from "../../../utils/authTokenStorage";
import { safeLocalStorageClear } from "../../../utils/localStorage";

const UserDetailDropdown = () => {
  const iconColor = "#f38321";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state, dispatch } = React.useContext(GoFlyerAdminContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMobileScreen = useMediaQuery("(max-width:600px)");
  const requiredGroups = [
    "user",
    "unit",
    "role",
    "merchant",
    "store",
    "notification",
    "flyerScraper",
    "elasticTrigger",
    "cache",
    "feedback",
    "tag",
  ];
  // Function to extract initials from the name
  const getInitials = (name: string) => {
    const nameParts = name.split(" ");
    if (nameParts.length === 1) return nameParts[0].charAt(0);
    return nameParts[0].charAt(0) + nameParts[1].charAt(0);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (value: string) => {
    navigate(value);
    handleClose();
  };

  const handleSignOut = () => {
    dispatch(setUser(undefined));
    clearAuthToken();
    safeLocalStorageClear();
    handleClose();
  };

  const renderMenuItems = () => (
    <div>
      <MenuItem>
        <ListItemIcon>
          <AccountCircleIcon fontSize="small" style={{ color: iconColor }} />
        </ListItemIcon>
        {state.user?.userName}
      </MenuItem>
      <MenuItem>
        <ListItemIcon>
          <BadgeIcon fontSize="small" style={{ color: iconColor }} />
        </ListItemIcon>
        {state.user?.parsedRoles?.name === SUPERADMIN
          ? "Super Admin"
          : state.user?.firstName + " " + state.user?.lastName || ""}
      </MenuItem>
      {state.user?.parsedRoles?.name !== SUPERADMIN && state.user?.email && (
        <MenuItem>
          <ListItemIcon>
            <EmailIcon fontSize="small" style={{ color: iconColor }} />
          </ListItemIcon>
          {state.user?.email}
        </MenuItem>
      )}
      <MenuItem>
        <ListItemIcon>
          <WorkspacePremiumIcon fontSize="small" style={{ color: iconColor }} />
        </ListItemIcon>
        {state.user?.parsedRoles?.name === SUPERADMIN
          ? SUPERADMIN
          : state.user?.roles[0]?.name}
      </MenuItem>

      {state.user?.parsedRoles?.name === SUPERADMIN &&
      !state.gfAdminUser?.settingsPage ? null : (
        <PermissionComponentWrapper
          requiredPermission={["view", "create", "edit", "delete"]}
          requiredPermissionGroup={requiredGroups}
          wrapperCss="display: inline-block;width:100%;"
        >
          <MenuItem
            onClick={() => {
              let route: string = routes.user.view;
              const foundUserPermission =
                state.user?.parsedRoles?.permissions.find(
                  (val) => val.groupName === "user",
                );
              if (!foundUserPermission) {
                const findAccountPage =
                  state.user?.parsedRoles?.permissions?.find((val) =>
                    requiredGroups.some((value) => val.groupName === value),
                  );
                if (findAccountPage) {
                  route = routes.dynamic.viewForGroup(
                    findAccountPage.groupName,
                  );
                }
              }
              handleNavigate(route);
            }}
            data-testid="top-nav-setup-button"
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" style={{ color: iconColor }} />
            </ListItemIcon>
            {t(messages.Setup())}
          </MenuItem>
        </PermissionComponentWrapper>
      )}

      <MenuItem
        onClick={() => handleNavigate("/adminUserSettings/view")}
        data-testid="top-nav-my-settings-button"
      >
        <ListItemIcon>
          <ManageAccountsIcon fontSize="small" style={{ color: iconColor }} />
        </ListItemIcon>
        My Settings
      </MenuItem>

      <MenuItem onClick={handleSignOut} data-testid="top-nav-sign-out-button">
        <ListItemIcon>
          <ExitToAppIcon fontSize="small" style={{ color: iconColor }} />
        </ListItemIcon>
        {t(messages.SignOut())}
      </MenuItem>
    </div>
  );

  return isMobileScreen ? (
    <div>{renderMenuItems()}</div>
  ) : (
    <>
      <IconButton
        onClick={handleClick}
        style={{ color: iconColor }}
        data-testid="top-nav-profile-dropdown"
      >
        <Avatar
          alt="User Avatar"
          style={{
            backgroundColor: iconColor,
            width: "32px",
            height: "32px",
            fontSize: "0.9rem",
          }}
        >
          {getInitials(
            state.user?.parsedRoles?.name === SUPERADMIN
              ? "Super Admin"
              : state.user?.firstName
                ? state.user.firstName + " " + state.user?.lastName
                : state?.user?.email
                  ? state.user.email
                  : "Goflyer",
          )}
        </Avatar>
        <ArrowDropDownIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          style: {
            borderRadius: 8,
          },
        }}
      >
        {renderMenuItems()}
      </Menu>
    </>
  );
};

export default UserDetailDropdown;
